import { useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';

// ============================================================
// useChatActions
// Owns: call (API wrapper) + all pure chat action handlers.
// Excludes game actions — those live in useGameActions.
// ============================================================
export default function useChatActions({
  matchId, token, fetchAll, isFetching, setError,
  setActiveSection, setSelectedAnswer, setFreeText,
  setIdentityAction, setShowSpotDialog, setChatClosed,
  freeText,
}) {
  // ── API WRAPPER ──
  const call = useCallback(async (method, endpoint, data = {}) => {
    setError('');
    try {
      const res = await axios[method](`${API_BASE}/chat/${matchId}${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      isFetching.current = false;
      await fetchAll();
      return res.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      return null;
    }
  }, [matchId, token, fetchAll, isFetching, setError]);

  // ── MESSAGE HANDLERS ──
  const sendQuestion   = async key => { const r = await call('post', '/send/question', { questionKey: key }); if (r) setActiveSection(null); };
  const sendAnswer     = async (key, val) => { if (!val) return; await call('post', '/send/answer', { questionKey: key, answerValue: val }); setSelectedAnswer(''); };
  const sendQuickReply = async r => { await call('post', '/send/quick-reply', { reply: r }); setActiveSection(null); };

  const sendFreeText = async () => {
    const t = freeText.trim();
    if (!t || t.length > 150) return;
    const r = await call('post', '/send/free-text', { text: t });
    if (r) setFreeText('');
  };

  // ── SPOTTING HANDLERS ──
  const initiateSpotting = async () => call('post', '/spotting/initiate', {});
  const openSpotDialog   = () => { setIdentityAction(''); setShowSpotDialog(true); };
  const submitSpotAction = async action => {
    const r = await call('post', '/spotting/action', { action });
    if (r) { setShowSpotDialog(false); if (r.unmatch) setChatClosed(true); }
  };
  const confirmSpotting = async sawThem => call('post', '/spotting/confirm', { sawThem });

  // ── CHAT FLOW HANDLERS ──
  const skipToChat    = async () => call('post', '/skip-to-chat', {});
  const requestBuffer = async () => call('post', '/buffer-request', {});

  // ── MEET HANDLERS ──
  const askToMeet      = async () => call('post', '/meet/ask', {});
  const respondToMeet  = async response => { const r = await call('post', '/meet/respond', { response }); if (r?.unmatch) setChatClosed(true); };
  const confirmMeeting = async () => call('post', '/meeting/confirm', {});
  const handleUnmatch  = async () => { await call('post', '/unmatch', {}); };

  return {
    call,
    sendQuestion, sendAnswer, sendQuickReply, sendFreeText,
    initiateSpotting, openSpotDialog, submitSpotAction, confirmSpotting,
    skipToChat, requestBuffer,
    askToMeet, respondToMeet, confirmMeeting, handleUnmatch,
  };
}
